import React from 'react';
import Box from '@components/Atoms/Box';
import Dialog from '@components/Atoms/Dialog';
import Typography from '@components/Atoms/Typography';
import { FAMILY_LABELS, FAMILY_ORDER, orderByFamily } from './metricMeta';
import { Bucket, MetricMeta } from './types';
import { HEAT_LEVEL_COLOURS, ZERO_COLOUR } from './Accountability.style';

interface DefinitionsDialogProps {
  open: boolean;
  onClose: () => void;
  /** The report's activity types; empty until the report has loaded. */
  metrics: MetricMeta[];
  activeDayDescription: string | null;
  /** Activity types nobody in the current view used. */
  unusedSlugs: ReadonlySet<string>;
  bucket: Bucket;
  /** Active days per bucket that count as full (the colour scale's top). */
  activeDayTarget: number;
}

const sectionHeadingSx = { fontSize: '0.95rem', fontWeight: 600, mt: 2.5, mb: 1 };
const familyHeadingSx = { fontSize: '0.8rem', fontWeight: 600, mt: 1.5, mb: 0.5 };
const listSx = {
  m: 0,
  fontSize: '0.85rem',
  '& dt': { fontWeight: 600, mt: 1 },
  '& dd': { m: 0, color: 'text.secondary', lineHeight: 1.45 },
};

/**
 * Plain-language definitions, opened by click so they work with a keyboard and
 * on touch screens. Activity types come from the report, so none are hardcoded.
 */
const DefinitionsDialog: React.FC<DefinitionsDialogProps> = ({
  open,
  onClose,
  metrics,
  activeDayDescription,
  unusedSlugs,
  bucket,
  activeDayTarget,
}) => {
  const ordered = orderByFamily(metrics);
  const families = FAMILY_ORDER.filter((family) =>
    ordered.some((m) => m.family === family),
  );

  return (
    <Dialog
      open={open}
      onClose={onClose}
      title="Definitions"
      showActionButtons={false}
      maxWidth="sm"
      fullWidth
      // The dialog atom clips overflowing content, so let the whole dialog
      // scroll within the page instead of inside the paper.
      scroll="body"
    >
      <Typography component="h4" sx={{ ...sectionHeadingSx, mt: 0 }}>
        Reading the grid
      </Typography>
      <Box component="dl" sx={listSx}>
        <dt>Active day</dt>
        <dd>
          {activeDayDescription ||
            'A day on which the teacher did at least one of the activity types below.'}
        </dd>

        <dt>Colors</dt>
        <dd>
          <Box aria-hidden="true" sx={{ display: 'flex', gap: 0.5, my: 0.75 }}>
            {HEAT_LEVEL_COLOURS.map((colour) => (
              <Box
                key={colour}
                sx={{
                  width: '1.4rem',
                  height: '0.7rem',
                  borderRadius: '2px',
                  background: colour,
                  border: colour === ZERO_COLOUR ? '1px solid #DDD5CA' : 'none',
                }}
              />
            ))}
          </Box>
          {`Warm gray means none. Active-day rows use a fixed scale from 0 to ${activeDayTarget} per ${bucket}. Activity rows are shaded against their own busiest ${bucket}, so a dark cell means busy for that activity, not busy overall. A row with only zeros is grayed out.`}
        </dd>

        <dt>Collapsed and expanded rows</dt>
        <dd>
          District, school and teacher rows count active days. Open a teacher to see how
          many times they did each activity type, then open “By section” to split it by
          class.
        </dd>

        <dt>Total</dt>
        <dd>
          On an activity row, how many times it happened in the selected dates. On a
          district, school, teacher or section row, how many active days there were. Each
          day counts once, so a school&rsquo;s days don&rsquo;t add up to its
          teachers&rsquo; days.
        </dd>

        <dt>Last seen</dt>
        <dd>
          The teacher’s most recent sign-in. Signing in is not activity: it never colors a
          cell or makes a day active.
        </dd>

        <dt>Sections with the same name</dt>
        <dd>
          A class can be set up in several courses, one per curriculum, so the same period
          can appear more than once under a teacher. The tag after its name shows the
          curriculum, and “archived” marks a course that is no longer active. Hover the
          tag for the full course name.
        </dd>
      </Box>

      <Typography component="h4" sx={sectionHeadingSx}>
        Activity types
      </Typography>
      {families.length === 0 && (
        <Typography sx={{ fontSize: '0.85rem', color: 'text.secondary' }}>
          The activity types appear here once the report has loaded.
        </Typography>
      )}
      {families.map((family) => (
        <React.Fragment key={family}>
          <Typography component="h5" sx={familyHeadingSx}>
            {FAMILY_LABELS[family] ?? family}
          </Typography>
          <Box component="dl" sx={listSx}>
            {ordered
              .filter((metric) => metric.family === family)
              .map((metric) => (
                <React.Fragment key={metric.slug}>
                  <dt>
                    {metric.label}
                    {unusedSlugs.has(metric.slug) ? ' (not used in this view)' : ''}
                  </dt>
                  <dd>{metric.description}</dd>
                </React.Fragment>
              ))}
          </Box>
        </React.Fragment>
      ))}

      <Typography component="h4" sx={sectionHeadingSx}>
        Filters and toggles
      </Typography>
      <Box component="dl" sx={listSx}>
        <dt>Show: Active teachers or All</dt>
        <dd>
          Active teachers, the default, hides teachers and classes with no active day in
          the selected dates. All brings back archived classes, and shows every teacher
          while “Hide rows with no active days” is off.
        </dd>

        <dt>Hide unused activity types</dt>
        <dd>
          On by default. Hides an activity type only when nobody in the current view used
          it in the selected dates, and the number in brackets says how many that is. A
          single teacher’s zeros always stay visible.
        </dd>

        <dt>Hide rows with no active days</dt>
        <dd>
          On by default. Hides every school, teacher and class with no active day in the
          selected dates, whatever Show is set to. It never hides activity rows, and the
          district row always stays. Turn it off to see those rows, grayed out.
        </dd>

        <dt>Using the toggles together</dt>
        <dd>
          They never contradict each other. A teacher with no active days has no activity
          of any type, so hiding inactive teachers cannot change which activity types
          count as unused. “Hide rows with no active days” hides the same teachers and
          classes as Active teachers, plus schools with no active day. So while it is on,
          All only adds archived classes. Only All can change the totals, by bringing
          archived classes back into the report.
        </dd>

        <dt>School, Curriculum and Subject</dt>
        <dd>
          Narrow the report to matching classes. Each list always offers everything you
          have access to, whatever else is selected. Subject groups classes broadly: ELA,
          Math or Science.
        </dd>

        <dt>Expected active days</dt>
        <dd>
          How many active days in a {bucket} count as full. It changes the colors only,
          never the numbers.
        </dd>
      </Box>
    </Dialog>
  );
};

export default DefinitionsDialog;
